import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ApiError } from "@/services/BaseService";
import { useStartGuestDeletion } from "@/hooks/useDataDeletion";
import { DeletionJob, DeletionMode } from "@/types/data-deletion";
import { DeletionJobStatusView } from "./DeletionJobStatusView";

interface GuestDeletionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Fires once the started job reaches COMPLETED. */
  onCompleted?: (job: DeletionJob) => void;
}

type StartErrorAction =
  | { kind: "close-inflight" }
  | { kind: "toast-notfound" }
  | { kind: "inline"; message: string };

const classifyStartError = (error: unknown, t: TFunction): StartErrorAction => {
  if (error instanceof ApiError) {
    if (error.statusCode === 409) {
      return { kind: "close-inflight" };
    }
    if (error.statusCode === 404) {
      return { kind: "toast-notfound" };
    }
  }
  return {
    kind: "inline",
    message:
      error instanceof Error
        ? error.message
        : t("dataDeletion.confirm.mismatch", {
            what: t("dataDeletion.confirm.what.email"),
          }),
  };
};

/**
 * Manual SUPER_ADMIN entry point for guest erasure. Guests have no platform-level
 * screen (the only Guests UI is hotel-scoped and operated by hotel roles, which
 * can't call the SUPER_ADMIN-only endpoint), so the platform Deletions page lets
 * an admin start a wipe by guest ID + tenant ID + confirmation token.
 */
export const GuestDeletionDialog: React.FC<GuestDeletionDialogProps> = ({
  open,
  onOpenChange,
  onCompleted,
}) => {
  const { t } = useTranslation();
  const startGuest = useStartGuestDeletion();

  const [phase, setPhase] = useState<"form" | "status">("form");
  const [guestId, setGuestId] = useState("");
  const [tenantId, setTenantId] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [mode, setMode] = useState<DeletionMode>(DeletionMode.PURGE);
  const [serverError, setServerError] = useState<string | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);

  const isAnonymize = mode === DeletionMode.ANONYMIZE;

  const close = () => {
    setPhase("form");
    setGuestId("");
    setTenantId("");
    setConfirmation("");
    setMode(DeletionMode.PURGE);
    setServerError(null);
    setJobId(null);
    onOpenChange(false);
  };

  const canSubmit = Boolean(
    guestId.trim() && tenantId.trim() && confirmation.trim(),
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || startGuest.isPending) {
      return;
    }
    try {
      const job = await startGuest.mutateAsync({
        id: guestId.trim(),
        dto: {
          confirmation: confirmation.trim(),
          tenantId: tenantId.trim(),
          mode,
        },
      });
      toast.success(t("dataDeletion.job.toast.started"));
      setJobId(job.id);
      setServerError(null);
      setPhase("status");
    } catch (error) {
      const action = classifyStartError(error, t);
      if (action.kind === "close-inflight") {
        toast.error(
          t("dataDeletion.job.toast.inFlight", {
            target: t("dataDeletion.targetWord.GUEST"),
          }),
        );
        close();
      } else if (action.kind === "toast-notfound") {
        toast.error(t("dataDeletion.job.toast.notFound"));
      } else {
        setServerError(action.message);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto">
        {phase === "form" ? (
          <form
            onSubmit={(event) => void handleSubmit(event)}
            className="flex flex-col gap-5"
          >
            <DialogHeader>
              <div className="flex items-center gap-2 text-custom-destructive-dark">
                <AlertTriangle className="size-5" />
                <DialogTitle className="text-custom-destructive-dark">
                  {t("dataDeletion.guestStart.title")}
                </DialogTitle>
              </div>
            </DialogHeader>

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-medium leading-none">
                {t("dataDeletion.guestStart.modeLabel")}
              </span>
              <Select
                value={mode}
                onValueChange={(value) => setMode(value as DeletionMode)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={DeletionMode.PURGE}>
                    {t("dataDeletion.guestStart.modePurge")}
                  </SelectItem>
                  <SelectItem value={DeletionMode.ANONYMIZE}>
                    {t("dataDeletion.guestStart.modeAnonymize")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="rounded-lg border border-custom-destructive-dark/30 bg-custom-destructive-dark/5 p-4">
              <p className="text-sm text-custom-destructive-dark">
                {isAnonymize
                  ? t("dataDeletion.guestStart.leadAnonymize")
                  : t("dataDeletion.guestStart.leadPurge")}
              </p>
            </div>

            <Input
              label={t("dataDeletion.guestStart.guestId")}
              value={guestId}
              onChange={(event) => setGuestId(event.target.value)}
              autoComplete="off"
              autoFocus
            />
            <div className="flex flex-col gap-1.5">
              <Input
                label={t("dataDeletion.guestStart.tenantId")}
                value={tenantId}
                onChange={(event) => setTenantId(event.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t("dataDeletion.guestStart.tenantIdHelp")}
              </p>
            </div>
            <div className="flex flex-col gap-1.5">
              <Input
                label={t("dataDeletion.guestStart.confirmation")}
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                autoComplete="off"
              />
              <p className="text-xs text-muted-foreground">
                {t("dataDeletion.guestStart.confirmationHelp")}
              </p>
            </div>

            {serverError ? (
              <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
                {serverError}
              </div>
            ) : null}

            <DialogFooter>
              <Button
                type="button"
                variant="new_outline"
                onClick={close}
                disabled={startGuest.isPending}
              >
                {t("dataDeletion.confirm.cancel")}
              </Button>
              <Button
                type="submit"
                variant="new_destructive"
                disabled={!canSubmit || startGuest.isPending}
              >
                {(() => {
                  if (startGuest.isPending) {
                    return t("dataDeletion.guestStart.submitting");
                  }
                  return isAnonymize
                    ? t("dataDeletion.guestStart.submitAnonymize")
                    : t("dataDeletion.guestStart.submitPurge");
                })()}
              </Button>
            </DialogFooter>
          </form>
        ) : null}

        {phase === "status" && jobId ? (
          <DeletionJobStatusView
            key={jobId}
            jobId={jobId}
            onCompleted={onCompleted}
            onClose={close}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
